import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
import {
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  MONO_NUMERIC_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../i18n/index.js';
import React, { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Table } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';

import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import {
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useTemplates,
  useUpdateTemplateMutation,
} from '../hooks/useTemplates.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile, type GridColumnMeta } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import { byDate, byNumber, byOrder, byText } from '../../../shared/ui/tableSorters.js';
import { formatDateTime } from '../../../shared/format/index.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';

const { Text } = Typography;
const { TextArea } = Input;

type TemplateChannel = 'all' | 'email' | 'sms' | 'in_app';

interface TemplateRow {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'in_app';
  variables: string[];
  usageCount: number;
  subject?: string | null;
  body: string;
  createdAt: string;
}

// Порядок каналов для сортировки — тот же, что и в фильтре над таблицей.
const CHANNEL_ORDER: readonly TemplateRow['type'][] = ['email', 'sms', 'in_app'];

const TEMPLATE_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'name',
    get label() {
      return i18n.t('crm.features.columns.template');
    },
  },
  {
    key: 'type',
    get label() {
      return i18n.t('crm.features.columns.channel');
    },
  },
  {
    key: 'variables',
    get label() {
      return i18n.t('crm.features.columns.variables');
    },
  },
  {
    key: 'usageCount',
    get label() {
      return i18n.t('crm.features.columns.usage_count');
    },
  },
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.columns.created_at');
    },
    hiddenByDefault: true,
  },
];

function getRussianPluralCategory(count: number): 'one' | 'few' | 'many' {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
  return 'many';
}

function buildSnippet(template: TemplateRow): string {
  const parts = [template.subject ?? '', template.body].map((part) => part.trim()).filter(Boolean);
  return parts.join(' · ');
}

function TemplateEditor({
  template,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
}: {
  template: TemplateRow | null;
  isSaving: boolean;
  isDeleting: boolean;
  onSave: (template: TemplateRow, input: { body: string; subject?: string }) => Promise<void>;
  onDelete: (template: TemplateRow) => void | Promise<unknown>;
}): ReactElement {
  const { t } = useTranslation();
  const [body, setBody] = useState(template?.body ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    if (!template) return;
    try {
      setSaveError(null);
      await onSave(template, {
        body,
        ...(template.type === 'email' ? { subject } : {}),
      });
    } catch {
      setSaveError(
        i18n.t(
          'crm.features.features.templates.pages.templatespage.не_удалось_сохранить_шаблон_проверьте_изме',
        ),
      );
    }
  }

  return (
    <Card
      size="small"
      className="lg:sticky lg:top-0"
      style={{
        borderColor: 'var(--ant-color-border-secondary)',
        background: 'var(--ant-color-bg-container)',
      }}
      styles={{ body: { padding: 16 } }}
    >
      {template ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 15 }}>
                {t('crm.templates.editor_title')}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {template.name}
              </Text>
            </div>
            <Tag style={{ fontSize: 11 }} data-testid="template-editor-meta">
              {t('crm.templates.editor.usage_count', { count: template.usageCount })} ·{' '}
              {t(
                `crm.templates.editor.variables_count_${getRussianPluralCategory(template.variables.length)}` as never,
                {
                  count: template.variables.length,
                },
              )}
            </Tag>
          </div>

          {template.type === 'email' && (
            <Form.Item
              label={
                <label
                  htmlFor="template-subject"
                  style={{
                    fontSize: 11,
                    color: 'var(--ant-color-text-secondary)',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                  }}
                >
                  {t('crm.templates.subject')}
                </label>
              }
            >
              <Input
                id="template-subject"
                aria-label={t('crm.templates.subject')}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </Form.Item>
          )}

          <div>
            <Text
              style={{
                display: 'block',
                marginBottom: 8,
                color: 'var(--ant-color-text-secondary)',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {t('crm.templates.body')}
            </Text>
            <TextArea
              id="template-body"
              data-testid="template-editor-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              style={{ minHeight: 220, fontFamily: 'monospace', fontSize: 13 }}
              autoSize={{ minRows: 8, maxRows: 18 }}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {template.variables.join(', ') || t('crm.templates.no_variables')}
            </Text>
            <div className="flex items-center gap-2">
              <DeleteEntityButton
                label={i18n.t('crm.features.features.templates.pages.templatespage.удалить_шаблон')}
                title={i18n.t(
                  'crm.features.features.templates.pages.templatespage.удалить_шаблон_2',
                )}
                description={i18n.t(
                  'crm.features.features.templates.pages.templatespage.отправленные_по_нему_письма_останутся_шабл',
                )}
                loading={isDeleting}
                onConfirm={() => onDelete(template)}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={isSaving}
                disabled={isSaving}
                onClick={() => void handleSave()}
                data-testid="template-save"
              >
                {t('crm.templates.save')}
              </Button>
            </div>
          </div>
          {saveError !== null ? <Alert type="error" showIcon title={saveError} /> : null}
        </div>
      ) : (
        <QuietEmpty text={t('crm.templates.editor_empty')} testId="template-editor-empty" />
      )}
    </Card>
  );
}

export function TemplatesPage(): ReactElement {
  const { t } = useTranslation();
  // Фильтры живут в URL: ссылку на отфильтрованный список можно передать.
  const [searchParams, setSearchParams] = useSearchParams();
  const channelParam = searchParams.get('channel');
  const channel: TemplateChannel = (['email', 'sms', 'in_app'] as readonly string[]).includes(
    channelParam ?? '',
  )
    ? (channelParam as TemplateChannel)
    : 'all';
  const searchQuery = searchParams.get('q') ?? '';
  const setParam = (name: 'channel' | 'q', value: string | undefined): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === undefined || value === '') next.delete(name);
        else next.set(name, value);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };
  // Шаблоны сервер отдаёт целиком (без take) — фильтр канала держим на клиенте.
  const templatesQuery = useTemplates({});
  const updateMutation = useUpdateTemplateMutation();
  const deleteMutation = useDeleteTemplateMutation();
  const createMutation = useCreateTemplateMutation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm] = Form.useForm<{
    name: string;
    type: 'email' | 'sms' | 'in_app';
    subject?: string;
    body: string;
  }>();

  const templates = templatesQuery.data ?? [];
  // Быстрая заметка цепляется к отмеченным шаблонам — как в остальных гридах.
  usePublishSelection(
    'template',
    templates
      .filter((template) => selectedTemplateIds.includes(template.id))
      .map((template) => ({ id: template.id, label: template.name })),
  );

  const visibleTemplates = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return templates.filter((template) => {
      if (channel !== 'all' && template.type !== channel) return false;
      if (!normalizedQuery) return true;

      const haystack = [template.name, template.subject ?? '', template.body]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [channel, searchQuery, templates]);

  const selectedTemplate = useMemo(
    // Resolve against the unfiltered list: search/filter/page changes must not
    // rip the template being edited out from under the editor.
    () => templates.find((template) => template.id === selectedId) ?? null,
    [selectedId, templates],
  );

  const summary = useMemo(() => {
    return {
      total: templates.length,
      email: templates.filter((template) => template.type === 'email').length,
      sms: templates.filter((template) => template.type === 'sms').length,
      inApp: templates.filter((template) => template.type === 'in_app').length,
    };
  }, [templates]);

  const isFiltered = channel !== 'all' || searchQuery.trim().length > 0;
  const pagination = useDataViewPagination(visibleTemplates);
  const activeTemplate = selectedTemplate ?? pagination.paginatedItems[0] ?? null;

  // Pin the auto-selected first row: without this, typing in the search box
  // reshuffles the fallback and silently destroys unsaved edits in the editor.
  useEffect(() => {
    if (selectedId === null && activeTemplate) {
      setSelectedId(activeTemplate.id);
    }
  }, [selectedId, activeTemplate]);

  async function handleSave(
    template: TemplateRow,
    input: { body: string; subject?: string },
  ): Promise<void> {
    await updateMutation.mutateAsync({
      templateId: template.id,
      input,
    });
  }

  async function handleCreate(values: {
    name: string;
    type: 'email' | 'sms' | 'in_app';
    subject?: string;
    body: string;
  }): Promise<void> {
    let created: TemplateRow;
    try {
      setCreateError(null);
      created = await createMutation.mutateAsync({
        name: values.name.trim(),
        type: values.type,
        body: values.body.trim(),
        ...(values.type === 'email' && values.subject?.trim()
          ? { subject: values.subject.trim() }
          : {}),
      });
    } catch {
      setCreateError(
        i18n.t(
          'crm.features.features.templates.pages.templatespage.не_удалось_создать_шаблон_проверьте_поля_и',
        ),
      );
      return;
    }

    setIsCreateOpen(false);
    createForm.resetFields();
    setSelectedId(created.id);
  }

  function handleResetFilters(): void {
    // Один вызов setSearchParams: два подряд перезаписывают друг друга, и часть
    // фильтров остаётся в URL.
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('channel');
        next.delete('q');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  function openTemplate(template: TemplateRow): void {
    setSelectedId(template.id);
  }

  const columnMap: Record<string, ColumnType<TemplateRow>> = {
    name: {
      title: t('crm.templates.table.template'),
      dataIndex: 'name',
      key: 'name',
      sorter: byText<TemplateRow>((row) => row.name),
      render: (name: string, record) => (
        <div className="flex min-w-0 flex-col gap-1">
          <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 13 }}>
            {name}
          </Text>
          <Text
            type="secondary"
            ellipsis={{ tooltip: buildSnippet(record) }}
            style={{ maxWidth: 520, fontSize: 12 }}
          >
            {buildSnippet(record) || t('crm.templates.no_preview')}
          </Text>
        </div>
      ),
    },
    type: {
      title: t('crm.templates.channel_label'),
      dataIndex: 'type',
      key: 'type',
      width: 132,
      sorter: byOrder<TemplateRow, TemplateRow['type']>((row) => row.type, CHANNEL_ORDER),
      render: (type: TemplateRow['type']) => (
        <EntityStatusChip
          tone="default"
          style={{ margin: 0, fontSize: 11, textTransform: 'uppercase' }}
        >
          {t(`crm.templates.channel.${type}` as never)}
        </EntityStatusChip>
      ),
    },
    variables: {
      title: t('crm.templates.variables'),
      dataIndex: 'variables',
      key: 'variables',
      width: 220,
      render: (vars: string[]) => (
        <div className="flex flex-wrap gap-1">
          {vars.slice(0, 3).map((variable) => (
            <Tag key={variable} style={{ margin: 0, fontFamily: 'monospace', fontSize: 11 }}>
              {variable}
            </Tag>
          ))}
          {vars.length > 3 ? (
            <Tag style={{ margin: 0, fontSize: 11 }}>+{vars.length - 3}</Tag>
          ) : null}
          {vars.length === 0 ? (
            <Text disabled style={{ fontSize: 12 }}>
              —
            </Text>
          ) : null}
        </div>
      ),
    },
    createdAt: {
      title: i18n.t('crm.features.features.templates.pages.templatespage.дата_создания'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: byDate<TemplateRow>((row) => row.createdAt),
      render: (value: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(value)}
        </Text>
      ),
    },
    usageCount: {
      title: t('crm.templates.usage_count'),
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 130,
      align: 'right',
      sorter: byNumber<TemplateRow>((row) => row.usageCount),
      render: (count: number) => (
        <Text
          style={{
            color: 'var(--ant-color-text)',
            ...MONO_NUMERIC_STYLE,
            fontSize: 12,
            fontWeight: 550,
          }}
        >
          {count}
        </Text>
      ),
    },
  };

  const actionsColumn: ColumnType<TemplateRow> = {
    title: t('crm.templates.table.action'),
    key: 'action',
    width: 120,
    align: 'center',
    render: (_: unknown, record) => (
      <Space size="small">
        <EntityHistoryButton
          entity="template"
          entityId={record.id}
          title={i18n.t('crm.features.features.templates.pages.templatespage.история_шаблона')}
          subject={record.name}
          iconOnly
        />
        {/* Клик по строке открывает редактор — гасим его на кнопке удаления. */}
        <span
          onClick={(event) => event.stopPropagation()}
          role="presentation"
          className="inline-flex"
        >
          <DeleteEntityButton
            iconOnly
            label={i18n.t('crm.features.features.templates.pages.templatespage.удалить_шаблон_3')}
            title={i18n.t('crm.features.features.templates.pages.templatespage.удалить_шаблон_4')}
            description={i18n.t(
              'crm.features.features.templates.pages.templatespage.отправленные_по_нему_письма_останутся_шабл_2',
            )}
            loading={deleteMutation.isPending && deleteMutation.variables === record.id}
            onConfirm={() => deleteMutation.mutateAsync(record.id)}
          />
        </span>
      </Space>
    ),
  };

  const grid = useGridProfile('templates', TEMPLATE_GRID_COLUMNS);
  const ordered = useOrderedColumns<TemplateRow>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={t('crm.templates.title')}
        subtitle={
          <>
            <span data-testid="templates-visible-count">
              {visibleTemplates.length}{' '}
              {i18n.t('crm.features.features.templates.pages.templatespage.из')} {summary.total}
            </span>{' '}
            {i18n.tc('crm.counts.templates', visibleTemplates.length)} · Email {summary.email} · SMS{' '}
            {summary.sms} · {t('crm.templates.channel.in_app')} {summary.inApp}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <GridColumnsButton profile={grid} />
            <Button
              type="primary"
              aria-label={t('crm.templates.new')}
              icon={<PlusOutlined />}
              onClick={() => {
                setCreateError(null);
                setIsCreateOpen(true);
              }}
              data-testid="template-create-open"
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {t('crm.templates.new')}
            </Button>
            <Tooltip title={t('crm.templates.refresh')}>
              <Button
                aria-label={t('crm.templates.refresh')}
                icon={<ReloadOutlined />}
                onClick={() => void templatesQuery.refetch()}
                style={LIST_ICON_BUTTON_STYLE}
              />
            </Tooltip>
          </div>
        }
        testId="page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="templates-search"
              aria-label={t('crm.templates.search_label')}
              placeholder={t('crm.templates.search_placeholder')}
              value={searchQuery}
              onChange={(value) => setParam('q', value)}
            />
          }
        >
          <FilterSelect
            aria-label={t('crm.templates.channel_filter_label')}
            value={channel}
            onChange={(value) => setParam('channel', value === 'all' ? undefined : value)}
            options={[
              { value: 'all', label: t('crm.templates.filter.all') },
              { value: 'email', label: t('crm.templates.channel.email') },
              { value: 'sms', label: t('crm.templates.channel.sms') },
              { value: 'in_app', label: t('crm.templates.channel.in_app') },
            ]}
          />

          {isFiltered ? (
            <Button onClick={handleResetFilters} style={LIST_CREATE_BUTTON_STYLE}>
              {t('crm.templates.reset_filters')}
            </Button>
          ) : null}
        </FilterBar>

        <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:items-start">
          <div
            className="min-h-0 flex-1 lg:self-stretch crm-checkbox-square"
            style={LIST_SURFACE_STYLE}
          >
            {templatesQuery.isError ? (
              <div className="p-6">
                <ErrorState
                  title={t('crm.templates.load_error')}
                  fallbackDescription={t('crm.templates.load_error_description')}
                  onRetry={() => void templatesQuery.refetch()}
                />
              </div>
            ) : templatesQuery.isLoading ? (
              <div aria-busy="true" aria-label={t('crm.templates.loading')}>
                <QuietListSkeleton rows={6} />
              </div>
            ) : (
              <Table<TemplateRow>
                showSorterTooltip={TABLE_SORTER_TOOLTIP}
                dataSource={visibleTemplates}
                columns={ordered.columns}
                components={ordered.components}
                rowKey="id"
                size="small"
                rowSelection={{
                  selectedRowKeys: selectedTemplateIds,
                  onChange: (keys) => setSelectedTemplateIds(keys.map(String)),
                }}
                pagination={pagination.pagination}
                data-testid="template-list"
                onRow={(record) => ({
                  'data-testid': `template-row-${record.id}`,
                  style: {
                    cursor: 'pointer',
                    background:
                      activeTemplate?.id === record.id ? 'var(--ant-color-primary-bg)' : undefined,
                  },
                  onClick: () => openTemplate(record),
                })}
                locale={{
                  emptyText: (
                    <QuietEmpty
                      text={
                        isFiltered ? t('crm.templates.empty_filtered') : t('crm.templates.empty')
                      }
                    />
                  ),
                }}
                style={{ fontSize: 13 }}
              />
            )}
          </div>

          <aside className="w-full shrink-0 lg:w-[380px] xl:w-[420px]">
            <TemplateEditor
              key={activeTemplate?.id ?? 'empty'}
              template={activeTemplate}
              isSaving={updateMutation.isPending}
              isDeleting={deleteMutation.isPending}
              onSave={handleSave}
              onDelete={(template) => deleteMutation.mutateAsync(template.id)}
            />
          </aside>
        </div>
      </div>

      <Modal
        title={t('crm.templates.new')}
        open={isCreateOpen}
        onCancel={() => {
          setIsCreateOpen(false);
          setCreateError(null);
        }}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => void handleCreate(values)}
          initialValues={{ type: 'email' }}
        >
          <Form.Item
            name="name"
            label={t('crm.templates.name')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: i18n.t(
                  'crm.features.features.templates.pages.templatespage.введите_название_шаблона',
                ),
              },
            ]}
          >
            <Input
              id="template-new-name"
              aria-label={t('crm.templates.name')}
              placeholder={t('crm.templates.name_placeholder')}
            />
          </Form.Item>

          {/* Native select is required for Playwright selectOption() */}
          <Form.Item
            name="type"
            label={t('crm.templates.channel_label')}
            getValueFromEvent={(e: React.ChangeEvent<HTMLSelectElement>) => e.target.value}
          >
            <select
              id="template-new-type"
              aria-label={t('crm.templates.channel_label')}
              style={{
                width: '100%',
                height: 32,
                border: '1px solid var(--ant-color-border)',
                borderRadius: 8,
                padding: '0 8px',
                fontSize: 13,
                background: 'var(--ant-color-bg-elevated)',
                color: 'var(--ant-color-text)',
                outline: 'none',
              }}
            >
              <option value="email">{t('crm.templates.channel.email')}</option>
              <option value="sms">{t('crm.templates.channel.sms')}</option>
              <option value="in_app">{t('crm.templates.channel.in_app')}</option>
            </select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) =>
              getFieldValue('type') === 'email' ? (
                <Form.Item name="subject" label={t('crm.templates.subject')}>
                  <Input id="template-new-subject" aria-label={t('crm.templates.subject')} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="body"
            label={t('crm.templates.new_body')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: i18n.t(
                  'crm.features.features.templates.pages.templatespage.введите_текст_шаблона',
                ),
              },
            ]}
          >
            <TextArea
              id="template-new-body"
              aria-label={t('crm.templates.new_body')}
              autoSize={{ minRows: 5, maxRows: 12 }}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>
          <Divider />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsCreateOpen(false)}>{t('crm.common.cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createMutation.isPending}
              icon={<PlusOutlined />}
              data-testid="template-create-submit"
            >
              {t('crm.templates.create_submit')}
            </Button>
          </div>
          {createError !== null ? (
            <Alert type="error" showIcon title={createError} style={{ marginTop: 16 }} />
          ) : null}
        </Form>
      </Modal>
    </div>
  );
}
